import React, { useEffect, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import moment from "moment";
import ISerial_Workflow_Log from "../../models/ISerial_Workflow_Log";
import { ISerialWorkflowHistoryResponseDTO } from "../../models/DTO/Production/IProductionResponseDTO";
import * as ProductionService from "../../services/ProductionService";
import { ProductionAuthContext } from "../../services/ProductionService";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import { useAppContextNonNull } from "@contexts/AppContext";

interface SerialWorkflowHistoryProps {
    show: boolean;
    serialId: number | null;
    ctx: ProductionAuthContext;
    onClose: () => void;
}

const SerialWorkflowHistory: React.FC<SerialWorkflowHistoryProps> = ({ show, serialId, ctx, onClose }) => {
    const { supplierModel } = useAppContextNonNull();
    const [data, setData] = useState<ISerialWorkflowHistoryResponseDTO | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!show || !serialId) {
            setData(null);
            return;
        }

        const fetchHistory = async () => {
            setIsLoading(true);
            try {
                const result = await ProductionService.getSerialWorkflowHistory(ctx, serialId);
                setData(result);
            } catch (err: any) {
                console.error("Failed to fetch serial workflow history", err?.response?.status, err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchHistory();
    }, [show, serialId]);

    return (
        <Modal show={show} onHide={onClose} size="lg" centered className="pd-history-modal">
            <Modal.Header closeButton>
                <Modal.Title>
                    Serial Workflow History
                    {data?.serial && (
                        <span className="ms-2 text-muted" style={{ fontSize: "0.875rem" }}>
                            — {data.serial.value}
                        </span>
                    )}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {isLoading ? (
                    <div className="d-flex justify-content-center py-4">
                        <Spinner animation="border" />
                    </div>
                ) : !data ? (
                    <p className="text-muted text-center py-3">No data available</p>
                ) : (
                    <>
                        {data.serial.vendor_name && (
                            <div className="mb-3 p-2 rounded" style={{ backgroundColor: "var(--minor-bg-color)" }}>
                                <small className="text-muted">
                                    <OwLabel title="vendor" isSupplierVendorOverride={true} supplierModel={supplierModel} />
                                </small>{" "}
                                <strong>{data.serial.vendor_name}</strong>
                            </div>
                        )}

                        {data.history.length === 0 ? (
                            <p className="text-muted text-center py-3">No workflow history recorded</p>
                        ) : (
                            <div className="pd-timeline">
                                {data.history.map((log: ISerial_Workflow_Log, index: number) => (
                                    <div key={log.id || index} className="pd-timeline__entry">
                                        <div className="pd-timeline__marker" />
                                        <div className="pd-timeline__content">
                                            <div className="pd-timeline__step-name">
                                                {log.Serial_Workflow_Step?.name || `Step ${log.serial_workflow_step_id}`}
                                                {log.is_manual_override && (
                                                    <span className="badge bg-warning text-dark ms-2">Manual Override</span>
                                                )}
                                            </div>
                                            <div className="pd-timeline__meta">
                                                {log.Employee_User?.name && (
                                                    <span className="me-3">
                                                        <i className="fas fa-user me-1" />
                                                        {(log.Employee_User as any)?.name || "Unknown"}
                                                    </span>
                                                )}
                                                {log.machine_station && (
                                                    <span className="me-3">
                                                        <i className="fas fa-cog me-1" />
                                                        {log.machine_station}
                                                    </span>
                                                )}
                                                {log.from_serial_workflow_step_id != null &&
                                                    log.from_serial_workflow_step_id !== log.serial_workflow_step_id && (
                                                        <span className="me-3">
                                                            <i className="fas fa-right-left me-1" />
                                                            from{" "}
                                                            {log.From_Workflow_Step?.name || `Step ${log.from_serial_workflow_step_id}`}
                                                        </span>
                                                    )}
                                                <span>
                                                    <i className="fas fa-clock me-1" />
                                                    {moment(log.completed_at).format("YYYY-MM-DD hh:mm:ss A")}
                                                </span>
                                            </div>
                                            {log.notes && <div className="pd-timeline__notes">{log.notes}</div>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default SerialWorkflowHistory;
